import { Link } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { cn } from '@/utils/cn';
import type { EntityNavItem } from '@/config/entity-navigation';

interface EntitySidebarNavProps {
  backHref: string;
  backLabel: string;
  items: EntityNavItem[];
  activeTab: string;
  activeSection: string;
  activeSubsection: string;
  collapsed: boolean;
  showBackButton?: boolean;
  buildHref: (tab: string, section?: string, subsection?: string) => string;
  onItemClick?: () => void;
}

export function EntitySidebarNav({
  backHref,
  backLabel,
  items,
  activeTab,
  activeSection,
  activeSubsection,
  collapsed,
  showBackButton = true,
  buildHref,
  onItemClick,
}: EntitySidebarNavProps) {
  // All drilldown state is derived from URL - no local state needed
  const drilldown =
    items.find((i) => i.value === activeTab && !!i.children?.length) ?? null;

  const subDrilldown =
    drilldown?.children?.find(
      (c) => c.value === activeSection && !!c.children?.length
    ) ?? null;

  const hrefForTopLevel = (item: EntityNavItem) => {
    if (item.children?.length) {
      const firstChild = item.children[0];
      const firstGrandchild = firstChild.children?.[0];
      return buildHref(item.value, firstChild.value, firstGrandchild?.value);
    }
    return buildHref(item.value);
  };

  const hrefForChild = (
    parentItem: EntityNavItem,
    child: NonNullable<EntityNavItem['children']>[number]
  ) => {
    if (child.children?.length) {
      return buildHref(parentItem.value, child.value, child.children[0].value);
    }
    return buildHref(parentItem.value, child.value);
  };

  const drilldownBackHref = buildHref('overview');
  const subDrilldownBackHref = drilldown
    ? buildHref(drilldown.value, drilldown.children![0].value)
    : '';

  const backButton = showBackButton && (
    <>
      <Link
        to={backHref}
        onClick={onItemClick}
        className={cn(
          'flex items-center px-3 py-2 rounded-md transition-colors group hover:bg-accent',
          collapsed ? 'justify-center' : ''
        )}
      >
        <ArrowLeft
          className={cn(
            'shrink-0 h-4 w-4 text-muted-foreground transition-transform group-hover:-translate-x-0.5',
            collapsed ? '' : 'mr-2'
          )}
        />
        {!collapsed && (
          <span className="text-lg font-semibold text-foreground">
            {backLabel}
          </span>
        )}
      </Link>
      <div className="border-t border-border" />
    </>
  );

  const drillBackButton = (label: string, href: string) => (
    <>
      <Link
        to={href}
        onClick={onItemClick}
        className="w-full flex items-center px-3 py-2 rounded-md transition-colors group hover:bg-accent"
      >
        <ArrowLeft className="shrink-0 h-4 w-4 text-muted-foreground mr-2 transition-transform group-hover:-translate-x-0.5" />
        <span className="text-lg font-semibold text-foreground">{label}</span>
      </Link>
      <div className="border-t border-border" />
    </>
  );

  // --- LEVEL 2: sub-drilldown (e.g. Drawdowns, Fees & Costs) ---
  if (subDrilldown && !collapsed) {
    return (
      <div>
        {backButton}
        {drillBackButton(subDrilldown.label, subDrilldownBackHref)}
        <nav className="space-y-0.5 pt-1">
          {subDrilldown.children!.map((grandchild) => {
            const isActive = activeSubsection === grandchild.value;
            return (
              <Link
                key={grandchild.value}
                to={buildHref(
                  drilldown!.value,
                  subDrilldown.value,
                  grandchild.value
                )}
                onClick={onItemClick}
                className={cn(
                  'w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                  isActive
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                )}
              >
                <span className="flex-1 text-left">{grandchild.label}</span>
              </Link>
            );
          })}
        </nav>
      </div>
    );
  }

  // --- LEVEL 1: drilldown (e.g. Transactions, Companies) ---
  if (drilldown && !collapsed) {
    return (
      <div>
        {backButton}
        {drillBackButton(drilldown.label, drilldownBackHref)}
        <nav className="space-y-0.5 pt-1">
          {drilldown.children!.map((child) => {
            const isActive = activeSection === child.value;
            return (
              <Link
                key={child.value}
                to={hrefForChild(drilldown, child)}
                onClick={onItemClick}
                className={cn(
                  'w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                  isActive
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                )}
              >
                <span className="flex-1 text-left">{child.label}</span>
              </Link>
            );
          })}
        </nav>
      </div>
    );
  }

  // --- LEVEL 0: top-level items (or collapsed state) ---
  return (
    <div>
      {backButton}
      <nav className="space-y-0.5 pt-1">
        {items.map((item) => {
          const isActive = activeTab === item.value;
          return (
            <Link
              key={item.value}
              to={hrefForTopLevel(item)}
              onClick={onItemClick}
              className={cn(
                'w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                collapsed ? 'justify-center' : '',
                isActive
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              )}
            >
              <item.icon
                className={cn(
                  'shrink-0 h-5 w-5',
                  collapsed ? '' : 'mr-3',
                  isActive ? 'opacity-100' : 'opacity-60'
                )}
              />
              {!collapsed && (
                <span className="flex-1 text-left">{item.label}</span>
              )}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
